/**
 * @fileoverview Own the shared native action control, including pending, severity and icon presentation. Callers own action execution and permission decisions; AppIcon resolves visual identity. All application workspaces use this boundary rather than a third-party button implementation.
 */

import React from 'react';
import AppIcon from './AppIcon';

const variantStyles = {
  primary: { backgroundColor: 'var(--primary-color)', color: '#ffffff', border: '1px solid var(--primary-color)' },
  secondary: { backgroundColor: 'var(--kc-surface-control, var(--surface-card))', color: 'var(--text-color)', border: '1px solid var(--kc-surface-edge-strong, var(--surface-border))' },
  success: { backgroundColor: '#16a34a', color: '#ffffff', border: '1px solid #15803d' },
  info: { backgroundColor: '#0ea5e9', color: '#ffffff', border: '1px solid #0284c7' },
  danger: { backgroundColor: '#dc2626', color: '#ffffff', border: '1px solid #b91c1c' }
};

const baseStyle = {
  borderRadius: '6px',
  padding: '0.5rem 1rem',
  fontWeight: 600,
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.5rem',
  fontSize: '0.875rem',
  lineHeight: 1.5,
  transition: 'opacity 0.15s'
};



function AppButton({ label, icon, onClick, disabled, variant = 'primary', severity, loading, loadingIcon,
  text, outlined, link, rounded, raised, plain, size, iconPos, tooltip, tooltipOptions, badge, badgeClassName,
  className, type = 'button', autoFocus, children, style: extraStyle, ...rest }) {
  const palette = variantStyles[severity || variant] || variantStyles.primary;
  const unavailable = disabled || loading;
  const actionIcon = loading ? loadingIcon || 'pi pi-spin pi-spinner' : icon;
  const actionColor = (severity || variant) === 'secondary' ? palette.color : palette.backgroundColor;
  return (
    <button
      type={type}
      className={['app-button', className].filter(Boolean).join(' ')}
      style={{
        ...baseStyle,
        ...palette,
        ...(text || link ? { backgroundColor: 'transparent', borderColor: 'transparent', color: actionColor } : { minHeight: 40, boxShadow: '0 1px 2px rgba(0, 0, 0, 0.08)' }),
        ...(outlined ? { backgroundColor: 'transparent', color: actionColor } : {}),
        ...(size === 'small' ? { padding: '.35rem .65rem', fontSize: '.8rem' } : {}),
        ...(rounded ? { borderRadius: 999 } : {}),
        cursor: unavailable ? 'not-allowed' : 'pointer',
        opacity: unavailable ? 0.6 : 1,
        ...extraStyle
      }}
      onClick={onClick}
      disabled={unavailable}
      aria-busy={loading || undefined}
      title={tooltip}
      autoFocus={autoFocus}
      {...rest}
    >
      {actionIcon && iconPos !== 'right' && <AppIcon icon={actionIcon} aria-hidden="true" />}
      {label && <span>{label}</span>}
      {children}
      {actionIcon && iconPos === 'right' && <AppIcon icon={actionIcon} aria-hidden="true" />}
      {badge != null && <span className={badgeClassName}>{badge}</span>}
    </button>
  );
}

export default AppButton;
